import { graphql } from 'gatsby';
import { CURRENT_SITE } from '../../../config-sites';
import React, { useEffect } from 'react';
import { NewsroomArticleAside } from '../../layout/NewsroomArticle/NewsroomArticleAside/NewsroomArticleAside';
import { NewsroomArticleContent } from '../../layout/NewsroomArticle/NewsroomArticleContent/NewsroomArticleContent';
import { HeroImage } from '../../components/HeroImage/HeroImage';
import { SEO } from '../../layout/SEO';
import { processMetatags } from '../../shared/metaTags';
import { AnalyticsController } from '../../layout/Analytics';
import { Schema } from '../../components/Schema/Schema';
import './NewsroomArticle.scss';
import { helixAnalytics } from '../../shared/analytics/HelixAnalytics';

export default function NewsroomArticle({ data }) {
    const { nodeNewsroomArticle: page } = data;

    useEffect(() => {
        AnalyticsController.pageView({
            pageType: `Article Corporate`,
        });
        helixAnalytics.trackPageView(
            data.nodeNewsroomArticle.title,
            `Newsroom Article`
        );
    }, []);

    return (
        <>
            <div className="bg"></div>
            <HeroImage
                inputs={{
                    imageAlt:
                        page.relationships?.field_news_image
                            ?.field_media_image_1?.alt,
                    image: page.relationships?.field_news_image?.relationships
                        ?.field_media_image_1?.localFile,
                    hideArrows: true,
                }}
            />
            <div className="newsroom-layout kite-container">
                <NewsroomArticleContent node={page} />
                <NewsroomArticleAside node={page} />
            </div>
        </>
    );
}

export const Head = ({ data, pageContext }) => {
    const { nodeNewsroomArticle: page } = data;
    const schemaComponent =
        page.relationships?.field_schema?.relationships?.field_key_values?.find(
            (schema) => {
                return schema.type === `paragraph__custom_schema`;
            }
        ) ?? page.relationships?.field_schema?.relationships?.field_key_values;
    const previousArticle = pageContext?.previous;
    const nextArticle = pageContext?.next;
    const schemaAddons = {
        headline: page.title,
        image: page.relationships?.field_news_image?.relationships
            ?.field_media_image_1?.localFile?.url,
        datePublished: new Date(page.field_news_date).toISOString(),
    };

    return (
        <>
            {previousArticle && (
                <link
                    rel="prev"
                    href={CURRENT_SITE.siteUrl + previousArticle}
                />
            )}
            {nextArticle && (
                <link rel="next" href={CURRENT_SITE.siteUrl + nextArticle} />
            )}
            <SEO location={page.fields.url}>
                {processMetatags(page.metatag)}
                {schemaComponent && (
                    <Schema
                        node={schemaComponent}
                        schemaAddons={schemaAddons}
                        schemaType="article"
                    />
                )}
            </SEO>
        </>
    );
};

export const query = graphql`
    query ($id: String!) {
        nodeNewsroomArticle(id: { eq: $id }) {
            title
            field_news_date(formatString: "MMMM D, YYYY HH:mm")
            field_news_featured
            field_subtitle
            field_listicle
            field_summary
            relationships {
                field_schema {
                    relationships {
                        field_key_values {
                            type: __typename
                            ... on paragraph__custom_schema {
                                field_custom_json_schema
                            }
                            ... on paragraph__schema_entry {
                                field_value
                                field_key
                            }
                            ... on paragraph__schema_entry_faq {
                                field_value
                                field_title
                            }
                        }
                    }
                }
                field_news_category {
                    name
                }
                field_news_image {
                    field_media_image_1 {
                        alt
                    }
                    relationships {
                        field_media_image_1 {
                            localFile {
                                url
                                childImageSharp {
                                    ...ImageFullWidthSettings
                                }
                            }
                        }
                    }
                }
                field_related_articles {
                    id
                    title
                    field_subtitle
                    field_summary
                    fields {
                        url
                    }
                    relationships {
                        field_news_image {
                            field_media_image_1 {
                                alt
                            }
                            relationships {
                                field_media_image_1 {
                                    localFile {
                                        childImageSharp {
                                            ...ImageFullWidthSettings
                                        }
                                    }
                                    filemime
                                }
                            }
                        }
                        field_alternate_image {
                            field_media_image {
                                alt
                            }
                            relationships {
                                field_media_image {
                                    localFile {
                                        childImageSharp {
                                            gatsbyImageData
                                        }
                                    }
                                }
                            }
                        }
                        field_news_category {
                            name
                        }
                    }
                }
                field_components {
                    type: __typename
                    ...paragraph__article_body
                    ...paragraph__article_links
                    ...paragraph__image
                    ...paragraph__inline_callout
                    ...paragraph__key_takeaways
                    ...paragraph__leadership_promotion
                    ...paragraph__paragraph_header
                    ...paragraph__pull_quote_article
                    ...paragraph__sources_list
                    ...paragraph__video
                    ...paragraph__web_table
                    ...paragraph__white_space
                    ...paragraph__youtube_card
                }
            }
            metatag {
                tag
                attributes {
                    content
                    href
                    name
                    rel
                    property
                }
            }
            promote
            fields {
                url
            }
            body {
                value
            }
        }
    }
`;
